import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Video, HardDriveDownload, Loader } from "lucide-react";
import { Button } from "@/components/primitives/button";
import { toast } from "@/hooks/useToast";
import { formatFileSize } from "@/lib/utils";
import { downloadRecording } from "@/server/services/recordingService";
import { RecordingItem } from "@/server/types/umbra/meet";

/** Format a duration in seconds as `m:ss` (or `h:mm:ss` past an hour). */
function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export default function Recording({ recording }: { recording: RecordingItem }) {
  const { t, i18n } = useTranslation();
  const [downloading, setDownloading] = useState(false);

  const started = new Date(recording.startedAt);
  const dateLabel = Number.isNaN(started.getTime())
    ? "—"
    : started.toLocaleString(i18n.language, {
        dateStyle: "medium",
        timeStyle: "short",
      });

  // Jibri auto-recordings report `startedBy: "system"`, which is not a
  // meaningful human name — show a neutral label instead of the literal string.
  const startedByLabel =
    !recording.startedBy || recording.startedBy === "system"
      ? t("chatDetailsTabs.recordingStartedBySystem")
      : recording.startedBy;

  async function handleDownload() {
    if (downloading) {
      return;
    }
    setDownloading(true);
    try {
      await downloadRecording(recording.id);
    } catch {
      // Always show the generic notice — a raw axios/blob error (e.g. the
      // "{}" a blob-typed error response stringifies to) is neither
      // user-friendly nor safe to surface.
      toast({
        duration: 2000,
        description: t("chatDetailsTabs.recordingDownloadError"),
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  }

  const metaParts = [formatFileSize(recording.sizeBytes || 0)];
  if (recording.durationSec != null) {
    metaParts.push(formatDuration(recording.durationSec));
  }
  metaParts.push(startedByLabel);

  return (
    <div className="flex items-center gap-2 p-1">
      <div className="relative flex size-10 shrink-0 items-center justify-center rounded-sm text-secondary">
        <Video strokeWidth={1} className="size-full" />
      </div>

      <div className="flex w-full min-w-0 flex-1 flex-col overflow-hidden">
        <span className="truncate text-sm font-medium">{dateLabel}</span>
        <span className="truncate text-xs text-muted-2">
          {metaParts.join(" · ")}
        </span>
      </div>

      <Button
        type="button"
        aria-label={t("chatDetailsTabs.recordingDownload")}
        onClick={handleDownload}
        disabled={downloading}
        size="icon"
        variant="ghost"
        className="ml-2 shrink-0 text-secondary opacity-70"
      >
        {downloading ? (
          <Loader size={16} className="animate-spin" />
        ) : (
          <HardDriveDownload size={16} />
        )}
      </Button>
    </div>
  );
}
